// import { getUFOConfig } from './feature-flags';

type Capability = "feature_flag_access" | "react_profiler";
type InteractionKind =
    | "page_load"
    | "transition"
    | "press"
    | "typing"
    | "legacy";
type UFONameOverride = {
    readonly [ufoName: string]: {
        readonly [metricKey: string]: string;
    };
};
// Defensively typed, since this is directly user-editable
// and they could delete empty members
type Config = {
    readonly awaitBM3TTI?: string[];
    readonly ufoNameOverrides?: UFONameOverride;
    readonly rates?: {
        readonly [key: string]: number;
    };
    readonly rules?: readonly {
        readonly test?: string;
        readonly rate?: number;
    }[];
    readonly killswitch?: readonly string[];
    readonly typingMethod?: string;
    readonly capability?: Record<Capability, number>;
    readonly kind?: Record<InteractionKind, number>;
    readonly removePageSegmentsUFOPrefixes?: boolean;
    readonly removeInteractionsUFOPrefixes?: boolean;
};

const config: Config = {
    removePageSegmentsUFOPrefixes: false,
    killswitch: [],
    rates: {
        // "issue-view-activity-button-worklog": 1,
    },
    kind: {
        legacy: 1,
        page_load: 1,
        press: 1,
        transition: 1,
        typing: 0
    },
    awaitBM3TTI: [],
    rules: [],
    capability: {
        feature_flag_access: 100,
        react_profiler: 1
    },
    typingMethod: "timeout",
    removeInteractionsUFOPrefixes: true
};

export function getInteractionRate(
    name: string,
    interactionKind: InteractionKind
): number {
    try {
        // REMOVED: const config: Config = getUFOConfig();
        const { killswitch, rates, rules, kind } = config;

        if (killswitch != null) {
            // Specifically kill certain events
            if (killswitch.includes(name)) {
                return 0;
            }
        }

        if (rates != null) {
            // Decide the rate explicitly
            if (rates[name] != null) {
                const ratesName = rates[name];
                if (ratesName) {
                    return ratesName;
                }
            }
        }

        if (rules != null) {
            // eslint-disable-next-line @typescript-eslint/prefer-for-of
            for (let i = 0; i < rules.length; i++) {
                const rule = rules[i];
                if (rule) {
                    const { test, rate } = rule;
                    if (test != null && rate != null) {
                        const regexp = new RegExp(test, "ig");

                        if (regexp.test(name)) {
                            return rate;
                        }
                    }
                }
            }
        }

        if (kind != null) {
            const rate = kind[interactionKind];
            if (rate != null) {
                return rate;
            }
        }

        return 0;
    } catch (e) {
        // Fallback
        return 0;
    }
}

export function getCapabilityRate(capability: Capability): number {
    try {
        // REMOVED: const config: Config = getUFOConfig();
        const { capability: capabilityRate } = config;
        if (capabilityRate != null) {
            const rate = capabilityRate[capability];
            if (rate != null) {
                return rate;
            }
        }
        return 0;
    } catch {
        return 0;
    }
}

const validTypingMethods = [
    "timeout",
    "timeoutNoAlloc",
    "mutationObserver"
] as const;
type ValidTypingMethod = (typeof validTypingMethods)[number];

export function getTypingPerformanceTracingMethod(): ValidTypingMethod {
    const defaultMethod = "timeout";
    try {
        // REMOVED: const config: Config = getUFOConfig();
        const { typingMethod } = config;

        if (
            typingMethod != null &&
            validTypingMethods.find((m) => m === typingMethod)
        ) {
            return typingMethod as ValidTypingMethod;
        } else {
            return defaultMethod;
        }
    } catch (e) {
        return defaultMethod;
    }
}

// Will ask UFO to wait for BM3 TTI for all events on this list
export function getAwaitBM3TTIList(): string[] {
    try {
        // REMOVED: const config: Config = getUFOConfig();
        const { awaitBM3TTI } = config;

        if (awaitBM3TTI != null) {
            return awaitBM3TTI;
        } else {
            return [];
        }
    } catch (e) {
        return [];
    }
}

// Flag to remove ufo segments prefixes
export function getRemovePageSegmentsUFOPrefixes(): boolean {
    try {
        // REMOVED: const config: Config = getUFOConfig();
        const { removePageSegmentsUFOPrefixes } = config;

        if (removePageSegmentsUFOPrefixes != null) {
            return removePageSegmentsUFOPrefixes;
        } else {
            return false;
        }
    } catch (e) {
        return false;
    }
}

// Flag to remove ufo segments prefixes
export function getRemoveInteractionsUFOPrefixes(): boolean {
    try {
        // REMOVED: const config: Config = getUFOConfig();;
        const { removeInteractionsUFOPrefixes } = config;

        if (removeInteractionsUFOPrefixes != null) {
            return removeInteractionsUFOPrefixes;
        } else {
            return false;
        }
    } catch (e) {
        return false;
    }
}

// Will ask UFO to override ufoName => experienceKey to the apdex key
// This is to cover use cases where one rUFO route is split into multiple BM3 keys (1:N)
// This workaround should only be used as last resource,
// when we find router limitations preventing teams from splliting these routes
export function getUfoNameOverrides(): UFONameOverride | undefined {
    try {
        // REMOVED: const config: Config = getUFOConfig();
        const { ufoNameOverrides } = config;
        if (ufoNameOverrides != null) {
            return ufoNameOverrides;
        }
        return undefined;
    } catch (e) {
        return undefined;
    }
}
